import { Box, Drawer, IconButton, SvgIconTypeMap, Typography, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import MainPanel from "./MainPanel";
import PreferencesPanel from "./PreferencesPanel";
import SettingsOptions from "./SettingOptions";

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import AppsIcon from '@mui/icons-material/Apps';
import PaidIcon from '@mui/icons-material/Paid';
import TuneIcon from '@mui/icons-material/Tune';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { colors } from "../../theme";

interface Option {
    name: string;
    icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
}

const settingOptions: Option[] = [
    { name: "Connected Apps", icon: AppsIcon },
    { name: "Preferences", icon: TuneIcon },
    { name: "Account", icon: AccountCircleIcon },
    { name: "Appearance", icon: ColorLensIcon },
    { name: "Subscription", icon: PaidIcon },
];

/** Categories with no backend behind them yet get an honest empty state. */
function ComingSoonPanel({ title }: { title: string }) {
    return (
        <Box sx={{ px: { xs: 3, md: 6 }, py: 6, maxWidth: 760 }}>
            <Typography variant="h3" sx={{ mb: 1.5 }}>{title}</Typography>
            <Typography variant="body1" sx={{ color: colors.textSecondary }}>
                Nothing to configure here yet — this section is still being built.
            </Typography>
        </Box>
    );
}

export default function PanelFrame() {
    const [selected, setSelected] = React.useState<string>("Connected Apps");
    // Same fix as the dashboard's library sidebar: below `sm` the categories list
    // and the panel content can't both fit, so the list becomes a slide-over instead
    // of a permanent column and the panel always gets the full width.
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const handleSelect = (name: string) => {
        setSelected(name);
        if (isMobile) setMobileMenuOpen(false);
    };

    const categoryList = (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: colors.textSecondary }}>
                    Settings
                </Typography>
                {isMobile && (
                    <IconButton
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Close settings menu"
                        sx={{ color: colors.textSecondary }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ px: 1 }}>
                {settingOptions.map((option) => (
                    <SettingsOptions
                        key={option.name}
                        settingCategory={option.name}
                        icon={option.icon}
                        selected={selected === option.name}
                        onSelect={() => handleSelect(option.name)}
                    />
                ))}
            </Box>
        </>
    );

    return (
        <Box sx={{ display: "flex", gap: 1, height: "100%", p: 1, backgroundColor: colors.sidebar }}>
            {isMobile ? (
                <Drawer
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: { width: 260, maxWidth: "85vw", backgroundColor: colors.base, py: 2 },
                    }}
                >
                    {categoryList}
                </Drawer>
            ) : (
                <Box
                    component="aside"
                    sx={{
                        width: { sm: 260, lg: 300 },
                        flexShrink: 0,
                        borderRadius: 2,
                        backgroundColor: colors.base,
                        overflowY: "auto",
                        py: 2,
                    }}
                >
                    {categoryList}
                </Box>
            )}

            <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {isMobile && (
                    <IconButton
                        onClick={() => setMobileMenuOpen(true)}
                        aria-label="Open settings menu"
                        sx={{
                            alignSelf: "flex-start",
                            m: 1,
                            color: colors.textSecondary,
                            backgroundColor: colors.base,
                            "&:hover": { backgroundColor: colors.elevated },
                        }}
                    >
                        <MenuIcon fontSize="small" />
                    </IconButton>
                )}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        borderRadius: 2,
                        backgroundColor: colors.base,
                        overflowY: "auto",
                    }}
                >
                    {selected === "Connected Apps" && <MainPanel />}
                    {selected === "Preferences" && <PreferencesPanel />}
                    {selected !== "Connected Apps" && selected !== "Preferences" && (
                        <ComingSoonPanel title={selected} />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
